import { Typography } from '@mui/material';
import { formatNumberUsingUnit } from '../../../utils/formatUtils';
import {
  DOWNLOAD_FILE_NAME,
  DOWNLOAD_FILE_NAME_LOCAL_SPEND,
  SUB_BRANCH_DASH_DOWNLOAD_FILE_NAME_LOCAL_SPEND,
  SUB_BRANCH_DASH_OPPS_BY_CHANNEL_DOWNLOAD_FILE_NAME,
  TOTAL,
  YOY_TOTAL,
} from '../constants';

const MATCHBACK_TOTAL_EXCLUDED_KEYS = ['close_rate', 'ajs', 'cpo', 'romi'];

const getSafeDivisionValue = (numerator, denominator, multiplier = 1) => {
  if (!isNumber(numerator) || !isNumber(denominator) || Number(denominator) === 0) {
    return undefined;
  }

  return (Number(numerator) / Number(denominator)) * multiplier;
};

// to see if the input is valid
const isNumber = (value) => {
  // Check if the value is not null, undefined, or an empty string
  return value != null && value !== '' && !isNaN(parseFloat(value));
};

// to render the cell content
const renderCellContent = (isValidValue, cellValue, parameter, channelName) => {
  const totalBoolean = [TOTAL, YOY_TOTAL].includes(channelName);
  const content = isValidValue ? formatNumberUsingUnit(cellValue, parameter) : 'N/A';
  return (
    <div style={{ textAlign: 'center' }}>
      <span style={{ fontWeight: totalBoolean ? '700' : 'normal' }}>{content}</span>
    </div>
  );
};

export const transformHeaderArray = [
  {
    Header: 'Channel Name',
    accessor: 'Channel_Name',
  },
  {
    Header: `Opps`,
    accessor: 'Sum_Of_Opps',
    Cell: ({ cell }) =>
      renderCellContent(isNumber(cell.value), cell.value, '', cell?.row?.original?.Channel_Name),
  },
  {
    Header: `Planned Opps`,
    accessor: 'Planned_Opps',
    Cell: ({ cell }) =>
      renderCellContent(isNumber(cell.value), cell.value, '', cell?.row?.original?.Channel_Name),
  },
  {
    Header: `Cancellation Rate`,
    accessor: 'Cancellation_Rate_Gross',
    Cell: ({ cell }) =>
      renderCellContent(isNumber(cell.value), cell.value, '%', cell?.row?.original?.Channel_Name),
  },
  {
    Header: 'Close Rate',
    accessor: 'Close_Rate_Gross',
    Cell: ({ cell }) =>
      renderCellContent(isNumber(cell.value), cell.value, '%', cell?.row?.original?.Channel_Name),
  },
  {
    Header: 'AJS',
    accessor: 'Average_Job_Size',
    Cell: ({ cell }) =>
      renderCellContent(isNumber(cell.value), cell.value, '$', cell?.row?.original?.Channel_Name),
  },
  {
    Header: 'Won Opps',
    accessor: 'Sum_Of_Won_Opps',
    Cell: ({ cell }) =>
      renderCellContent(isNumber(cell.value), cell.value, '', cell?.row?.original?.Channel_Name),
  },
  {
    Header: 'Revenue',
    accessor: 'Sum_Of_Revenue',
    Cell: ({ cell }) =>
      renderCellContent(isNumber(cell.value), cell.value, '$', cell?.row?.original?.Channel_Name),
  },
  {
    Header: 'Spend',
    accessor: 'Spend',
    Cell: ({ cell }) =>
      renderCellContent(isNumber(cell.value), cell.value, '$', cell?.row?.original?.Channel_Name),
  },
  {
    Header: 'CPO',
    accessor: 'CPO',
    Cell: ({ cell }) =>
      renderCellContent(isNumber(cell.value), cell.value, '$', cell?.row?.original?.Channel_Name),
  },
  {
    Header: 'Yield',
    accessor: 'Yield_Gross',
    Cell: ({ cell }) =>
      renderCellContent(isNumber(cell.value), cell.value, '$', cell?.row?.original?.Channel_Name),
  },
  {
    Header: 'ROMI',
    accessor: 'romi',
    Cell: ({ cell }) =>
      renderCellContent(isNumber(cell.value), cell.value, '$', cell?.row?.original?.Channel_Name),
  },
];

export const matchbackHeaderArray = [
  {
    Header: 'Channel Name',
    accessor: 'channel',
    Cell: ({ cell }) => cell?.row?.original?.channel ?? '',
  },
  {
    Header: 'Sent',
    accessor: 'sent',
    Cell: ({ cell }) =>
      renderCellContent(isNumber(cell.value), cell.value, '', cell?.row?.original?.channel),
  },
  {
    Header: 'Opps',
    accessor: 'opps',
    Cell: ({ cell }) =>
      renderCellContent(isNumber(cell.value), cell.value, '', cell?.row?.original?.channel),
  },
  {
    Header: 'Won Opps',
    accessor: 'won_opps',
    Cell: ({ cell }) =>
      renderCellContent(isNumber(cell.value), cell.value, '', cell?.row?.original?.channel),
  },
  {
    Header: 'Close Rate',
    accessor: 'close_rate',
    Cell: ({ cell }) =>
      renderCellContent(isNumber(cell.value), cell.value, '%', cell?.row?.original?.channel),
  },
  {
    Header: 'Revenue',
    accessor: 'won_amount',
    Cell: ({ cell }) =>
      renderCellContent(isNumber(cell.value), cell.value, '$', cell?.row?.original?.channel),
  },
  {
    Header: 'AJS',
    accessor: 'ajs',
    Cell: ({ cell }) =>
      renderCellContent(isNumber(cell.value), cell.value, '$', cell?.row?.original?.channel),
  },
  {
    Header: 'Spend',
    accessor: 'spend',
    Cell: ({ cell }) =>
      renderCellContent(isNumber(cell.value), cell.value, '$', cell?.row?.original?.channel),
  },
  {
    Header: 'CPO',
    accessor: 'cpo',
    Cell: ({ cell }) =>
      renderCellContent(isNumber(cell.value), cell.value, '$', cell?.row?.original?.channel),
  },
  {
    Header: 'ROMI',
    accessor: 'romi',
    Cell: ({ cell }) =>
      renderCellContent(isNumber(cell.value), cell.value, '$', cell?.row?.original?.channel),
  },
];

const vendorColumn = {
  Header: 'Vendor',
  accessor: 'Vendor',
  Cell: ({ cell }) => cell?.row?.original?.Vendor ?? '',
};

/**
 * Inserts a column after a given accessor in the array immutably.
 */
const insertColumnAfter = (array, accessorKey, columnToInsert) => {
  const index = array.findIndex((col) => col.accessor === accessorKey);
  if (index === -1) return array;
  return [...array.slice(0, index + 1), columnToInsert, ...array.slice(index + 1)];
};

/**
 * Exported transformed header array with 'Vendor' after 'Channel_Name'
 */
export const getTransformedHeaderArray = () =>
  insertColumnAfter(transformHeaderArray, 'Channel_Name', vendorColumn);

export const getLeadAggregationOppsHeaderArray = () => [
  vendorColumn,
  ...transformHeaderArray.filter((column) => column.accessor !== 'Channel_Name'),
];

const calculateDerivedMetrics = (acc) => {
  const totalOpps = Number(acc['Sum_Of_Opps']) || 0;
  const hasCancelledOpps = Object.prototype.hasOwnProperty.call(acc, 'Sum_Of_Cancelled_Opps');

  if (hasCancelledOpps && totalOpps > 0) {
    const totalCancelledOpps = Number(acc['Sum_Of_Cancelled_Opps']) || 0;
    acc['Cancellation_Rate_Gross'] = (totalCancelledOpps / totalOpps) * 100;
  }

  if (acc['Sum_Of_Net_Opps'] > 0 && acc['Sum_Of_Opps'] > 0) {
    acc['Net_Conversion'] = (acc['Sum_Of_Net_Opps'] / acc['Sum_Of_Opps']) * 100;
  }

  if (acc['Sum_Of_Won_Opps'] > 0 && acc['Sum_Of_Opps'] > 0) {
    acc['Close_Rate_Gross'] = (acc['Sum_Of_Won_Opps'] / acc['Sum_Of_Opps']) * 100;
  }

  if (acc['Sum_Of_Revenue'] > 0 && acc['Sum_Of_Won_Opps'] > 0) {
    acc['Average_Job_Size'] = acc['Sum_Of_Revenue'] / acc['Sum_Of_Won_Opps'];
  }
  if (acc['Spend'] > 0 && acc['Sum_Of_Opps'] > 0) {
    acc['CPO'] = acc['Spend'] / acc['Sum_Of_Opps'];
  }
  if (acc['Average_Job_Size'] > 0 && acc['Close_Rate_Gross'] > 0) {
    // acc['Yield_Gross'] = acc['Sum_Of_Revenue'] / acc['Sum_Of_Opps'];
    // AJS*Close Rate Gross
    acc['Yield_Gross'] = (acc['Average_Job_Size'] * acc['Close_Rate_Gross']) / 100;
  }
  if (acc['Spend'] > 0 && acc['Sum_Of_Revenue'] > 0) {
    acc['romi'] = acc['Sum_Of_Revenue'] / acc['Spend'];
  }
};

const calculateTotals = (data) => {
  const keysToExclude = [
    'Net_Conversion',
    'Cancellation_Rate_Gross',
    'Close_Rate_Gross',
    'Average_Job_Size',
    'CPO',
    'Yield_Gross',
    'Vendor',
  ];
  return data.reduce((acc, curr) => {
    for (const key in curr) {
      if (!isNaN(curr[key]) && !keysToExclude.includes(key)) {
        acc[key] = (acc[key] || 0) + curr[key];
      }
    }

    calculateDerivedMetrics(acc);
    return acc;
  }, {});
};

// To add the total of all the channels
export const addTotalObject = (data, totalLabelColumn) => {
  const totals = calculateTotals(data);
  const totalObject = {
    Channel_Name: TOTAL,
    ...(totalLabelColumn && { [totalLabelColumn]: TOTAL }),
    ...totals,
  };
  return [...data, totalObject];
};

const calculateMatchbackDerivedMetrics = (totals) => {
  totals.close_rate = getSafeDivisionValue(totals.won_opps, totals.opps, 100);
  totals.ajs = getSafeDivisionValue(totals.won_amount, totals.won_opps);
  totals.cpo = getSafeDivisionValue(totals.spend, totals.opps);
  totals.romi = getSafeDivisionValue(totals.won_amount, totals.spend);
};

export const getTransformedMatchbackRows = (data) => {
  if (!Array.isArray(data)) {
    return [];
  }

  return data.map((item) => ({
    sent: item?.sent,
    channel: item?.channel ?? 'N/A',
    opps: item?.opps_30,
    won_opps: item?.won_opps_30,
    close_rate: item?.close_rate_30,
    won_amount: item?.won_amount_30,
    ajs: item?.ajs_30,
    spend: item?.spend,
    cpo: item?.cpo_30,
    romi: item?.romi_30,
  }));
};

export const addMatchbackTotalObject = (data) => {
  const totals = data.reduce(
    (acc, curr) => {
      for (const key in curr) {
        if (!MATCHBACK_TOTAL_EXCLUDED_KEYS.includes(key) && isNumber(curr[key])) {
          acc[key] = (acc[key] || 0) + Number(curr[key]);
        }
      }

      return acc;
    },
    { channel: TOTAL }
  );

  calculateMatchbackDerivedMetrics(totals);

  return [...data, totals];
};

// for finding the minumum and maximum value form the data for particular key
const findMinMaxByKey = (data, key) => {
  // Create a copy of the array without the last object to avoid total object
  const modifiedArray = data.slice(0, -1);

  let min = Infinity;
  let max = -Infinity;

  for (const item of modifiedArray) {
    const value = item[key];
    if (value != null && !isNaN(value) && value > 0) {
      min = Math.min(min, value);
      max = Math.max(max, value);
    }
  }
  return { min, max };
};

// This function is used to calculate percentage
const calculatePercentage = (current, min, max) => {
  // Check if any of the values are null, undefined, or 0
  if (current == null || min == null || max == null) {
    return null;
  }
  // Check if min is greater than or equal to max
  if (min >= max) {
    return null;
  }
  // Calculate the percentage
  const percent = ((current - min) / (max - min)) * 100;
  return percent;
};

// percent value calculator for the pallete color
export const percentValueForPallete = (rowData, columnID, cellInfoValue) => {
  const { min, max } = findMinMaxByKey(rowData, columnID);
  // to return the dark red value if min amount
  if (min === max) {
    return 100;
  } else if (min === 0 && min === cellInfoValue) return { percent: 0, avgValueForColumn: 1 };

  const avgValueForColumn = rowData?.filter((data) => data?.Channel_Name === 'Total')[0]?.[
    columnID
  ];

  return {
    percent: calculatePercentage(cellInfoValue, min, max),
    avgValueForColumn: calculatePercentage(avgValueForColumn, min, max),
  };
};

// Utility function to update filter values for compare v2
export const updateFilterValues = (filter, year, week, storeId) => {
  return filter?.map((filterValue) => {
    if (filterValue?.col === 'dateindex' && filterValue?.op === '>=') {
      return { ...filterValue, val: `${year}01` };
    }
    if (filterValue?.col === 'dateindex' && filterValue?.op === '<=') {
      return { ...filterValue, val: `${year}${week}` };
    }
    if (filterValue.col === 'store_id') {
      return { ...filterValue, val: storeId };
    }
    return filterValue;
  });
};

export const transformYOYChannelHeader = (currentYear = new Date().getFullYear()) => [
  {
    Header: () => <Typography sx={{ textAlign: 'left', flex: 1 }}>Channel Name</Typography>,
    accessor: 'Channel_Name',
  },
  {
    Header: `${currentYear - 1} Spend`,
    accessor: 'spend_previous',
    Cell: ({ cell }) =>
      renderCellContent(isNumber(cell.value), cell.value, '$', cell?.row?.original?.Channel_Name),
  },
  {
    Header: `${currentYear - 1} ROMI`,
    accessor: 'romi_previous',
    Cell: ({ cell }) =>
      renderCellContent(isNumber(cell.value), cell.value, '$', cell?.row?.original?.Channel_Name),
  },
  {
    Header: `${currentYear} Spend`,
    accessor: 'spend_current',
    Cell: ({ cell }) =>
      renderCellContent(isNumber(cell.value), cell.value, '$', cell?.row?.original?.Channel_Name),
  },
  {
    Header: `${currentYear} ROMI`,
    accessor: 'romi_current',
    Cell: ({ cell }) =>
      renderCellContent(isNumber(cell.value), cell.value, '$', cell?.row?.original?.Channel_Name),
  },
];

export const transformYOYCPOChannelHeader = (currentYear = new Date().getFullYear()) => [
  {
    Header: () => <Typography sx={{ textAlign: 'left', flex: 1 }}>Channel Name</Typography>,
    accessor: 'Channel_Name',
  },
  {
    Header: `${currentYear - 1} Spend`,
    accessor: 'spend_previous',
    Cell: ({ cell }) =>
      renderCellContent(isNumber(cell.value), cell.value, '$', cell?.row?.original?.Channel_Name),
  },
  {
    Header: `${currentYear - 1} CPO`,
    accessor: 'cpo_previous',
    Cell: ({ cell }) =>
      renderCellContent(isNumber(cell.value), cell.value, '$', cell?.row?.original?.Channel_Name),
  },
  {
    Header: `${currentYear} Spend`,
    accessor: 'spend_current',
    Cell: ({ cell }) =>
      renderCellContent(isNumber(cell.value), cell.value, '$', cell?.row?.original?.Channel_Name),
  },
  {
    Header: `${currentYear} CPO`,
    accessor: 'cpo_current',
    Cell: ({ cell }) =>
      renderCellContent(isNumber(cell.value), cell.value, '$', cell?.row?.original?.Channel_Name),
  },
];

export const transformYTDMatchbackRomiHeader = (currentYear = new Date().getFullYear()) => [
  {
    Header: () => <Typography sx={{ textAlign: 'left', flex: 1 }}>Channel Name</Typography>,
    accessor: 'Channel_Name',
  },
  {
    Header: `${currentYear - 1} Spend`,
    accessor: 'spend_previous',
    Cell: ({ cell }) =>
      renderCellContent(isNumber(cell.value), cell.value, '$', cell?.row?.original?.Channel_Name),
  },
  {
    Header: `${currentYear - 1} ROMI`,
    accessor: 'romi_previous',
    Cell: ({ cell }) =>
      renderCellContent(isNumber(cell.value), cell.value, '$', cell?.row?.original?.Channel_Name),
  },
  {
    Header: `${currentYear} Spend`,
    accessor: 'spend_current',
    Cell: ({ cell }) =>
      renderCellContent(isNumber(cell.value), cell.value, '$', cell?.row?.original?.Channel_Name),
  },
  {
    Header: `${currentYear} ROMI`,
    accessor: 'romi_current',
    Cell: ({ cell }) =>
      renderCellContent(isNumber(cell.value), cell.value, '$', cell?.row?.original?.Channel_Name),
  },
];

const getYTDMatchbackYearKey = (fiscalYear, currentYear) => {
  if (fiscalYear === currentYear - 1) {
    return 'previous';
  }

  if (fiscalYear === currentYear) {
    return 'current';
  }

  return null;
};

export const transformYTDMatchbackRomiResponseData = (
  data,
  currentYear = new Date().getFullYear()
) => {
  if (!Array.isArray(data)) {
    return [];
  }

  const groupedData = data.reduce((accumulator, item) => {
    const channelName = item?.channel || 'N/A';
    const yearKey = getYTDMatchbackYearKey(Number(item?.fiscal_year), currentYear);

    if (!yearKey) {
      return accumulator;
    }

    if (!accumulator[channelName]) {
      accumulator[channelName] = {
        Channel_Name: channelName,
        spend_previous: 'N/A',
        spend_current: 'N/A',
        romi_previous: 'N/A',
        romi_current: 'N/A',
        won_amount_previous: 0,
        won_amount_current: 0,
      };
    }

    accumulator[channelName][`spend_${yearKey}`] = item?.spend ?? 'N/A';
    accumulator[channelName][`romi_${yearKey}`] = item?.romi_30 ?? 'N/A';
    accumulator[channelName][`won_amount_${yearKey}`] = isNumber(item?.won_amount_30)
      ? Number(item?.won_amount_30)
      : 0;

    return accumulator;
  }, {});

  const transformedData = Object.values(groupedData).sort((a, b) =>
    a?.Channel_Name?.localeCompare(b?.Channel_Name)
  );

  const grandTotal = transformedData.reduce(
    (accumulator, currentRow) => {
      if (currentRow.spend_previous !== 'N/A') {
        accumulator.spend_previous += currentRow.spend_previous;
      }
      if (currentRow.spend_current !== 'N/A') {
        accumulator.spend_current += currentRow.spend_current;
      }

      accumulator.won_amount_previous += currentRow.won_amount_previous || 0;
      accumulator.won_amount_current += currentRow.won_amount_current || 0;

      return accumulator;
    },
    {
      Channel_Name: YOY_TOTAL,
      spend_previous: 0,
      spend_current: 0,
      romi_previous: 0,
      romi_current: 0,
      won_amount_previous: 0,
      won_amount_current: 0,
    }
  );

  grandTotal.romi_previous =
    grandTotal.won_amount_previous > 0 && grandTotal.spend_previous > 0
      ? grandTotal.won_amount_previous / grandTotal.spend_previous
      : 'N/A';
  grandTotal.romi_current =
    grandTotal.won_amount_current > 0 && grandTotal.spend_current > 0
      ? grandTotal.won_amount_current / grandTotal.spend_current
      : 'N/A';
  grandTotal.spend_previous = grandTotal.spend_previous || 'N/A';
  grandTotal.spend_current = grandTotal.spend_current || 'N/A';

  return [...transformedData, grandTotal];
};

export const transformYOYChannelResponseData = (data) => {
  if (!Array.isArray(data)) {
    return [];
  }

  const transformedData = data?.map((item) => ({
    Channel_Name: item?.channel_name || 'N/A',
    romi_current: item?.romi?.current ?? 'N/A',
    romi_previous: item?.romi?.previous ?? 'N/A',
    spend_current: item?.spend?.current ?? 'N/A',
    spend_previous: item?.spend?.previous ?? 'N/A',
    cpo_current: item?.CPO?.current ?? 'N/A',
    cpo_previous: item?.CPO?.previous ?? 'N/A',
    // for the total calculation of romi and cpo these additional keys are addded
    sum_of_opps_current: item?.Sum_Of_Opps?.current ?? 'N/A',
    sum_of_opps_previous: item?.Sum_Of_Opps?.previous ?? 'N/A',
    sum_of_revenue_current: item?.Sum_Of_Revenue?.current ?? 'N/A',
    sum_of_revenue_previous: item?.Sum_Of_Revenue?.previous ?? 'N/A',
  }));

  // Sort the transformed data in ascending order based on Channel_Name
  transformedData?.sort((a, b) => a?.Channel_Name?.localeCompare(b?.Channel_Name));

  const grandTotal = transformedData?.reduce(
    (acc, curr) => {
      if (curr.romi_current !== 'N/A') acc.romi_current += curr?.romi_current;
      if (curr.romi_previous !== 'N/A') acc.romi_previous += curr?.romi_previous;
      if (curr.spend_current !== 'N/A') acc.spend_current += curr?.spend_current;
      if (curr.spend_previous !== 'N/A') acc.spend_previous += curr?.spend_previous;
      if (curr.cpo_current !== 'N/A') acc.cpo_current += curr?.cpo_current;
      if (curr.cpo_previous !== 'N/A') acc.cpo_previous += curr?.cpo_previous;
      if (curr.sum_of_opps_current !== 'N/A') acc.sum_of_opps_current += curr?.sum_of_opps_current;
      if (curr.sum_of_opps_previous !== 'N/A')
        acc.sum_of_opps_previous += curr?.sum_of_opps_previous;
      if (curr.sum_of_revenue_current !== 'N/A')
        acc.sum_of_revenue_current += curr?.sum_of_revenue_current;
      if (curr.sum_of_revenue_previous !== 'N/A')
        acc.sum_of_revenue_previous += curr?.sum_of_revenue_previous;
      return acc;
    },
    {
      Channel_Name: YOY_TOTAL,
      romi_current: 0,
      romi_previous: 0,
      spend_current: 0,
      spend_previous: 0,
      cpo_current: 0,
      cpo_previous: 0,
      sum_of_opps_current: 0,
      sum_of_opps_previous: 0,
      sum_of_revenue_current: 0,
      sum_of_revenue_previous: 0,
    }
  );

  // Convert totals to 'N/A' if they are zero
  grandTotal.romi_current =
    grandTotal?.sum_of_revenue_current > 0 && grandTotal?.spend_current > 0
      ? grandTotal?.sum_of_revenue_current / grandTotal?.spend_current
      : 'N/A';
  grandTotal.romi_previous =
    grandTotal?.sum_of_revenue_previous > 0 && grandTotal?.spend_previous > 0
      ? grandTotal?.sum_of_revenue_previous / grandTotal?.spend_previous
      : 'N/A';
  grandTotal.spend_current = grandTotal.spend_current || 'N/A';
  grandTotal.spend_previous = grandTotal.spend_previous || 'N/A';
  grandTotal.cpo_current =
    grandTotal?.spend_current > 0 && grandTotal?.sum_of_opps_current > 0
      ? grandTotal?.spend_current / grandTotal?.sum_of_opps_current
      : 'N/A';
  grandTotal.cpo_previous =
    grandTotal?.spend_previous > 0 && grandTotal?.sum_of_opps_previous > 0
      ? grandTotal?.spend_previous / grandTotal?.sum_of_opps_previous
      : 'N/A';
  grandTotal.sum_of_opps_current = grandTotal.sum_of_opps_current || 'N/A';
  grandTotal.sum_of_opps_previous = grandTotal.sum_of_opps_previous || 'N/A';
  grandTotal.sum_of_revenue_current = grandTotal.sum_of_revenue_current || 'N/A';
  grandTotal.sum_of_revenue_previous = grandTotal.sum_of_revenue_previous || 'N/A';

  return [...transformedData, grandTotal];
};

// Utility function to sort by Channel_Name, then by Vendor
export const sortByChannelAndVendor = (data) => {
  if (!Array.isArray(data)) return [];
  return [...data]?.sort((a, b) => {
    const channelCompare = (a?.Channel_Name || '')?.localeCompare(b?.Channel_Name || '');
    if (channelCompare !== 0) return channelCompare;
    return (a?.Vendor || '')?.localeCompare(b?.Vendor || '');
  });
};

export const getChannelOppsDownloadFilename = (isSpendToggleActive, isSubBranchDashboard) => {
  if (isSpendToggleActive) {
    return isSubBranchDashboard
      ? SUB_BRANCH_DASH_DOWNLOAD_FILE_NAME_LOCAL_SPEND
      : DOWNLOAD_FILE_NAME_LOCAL_SPEND;
  } else {
    return isSubBranchDashboard
      ? SUB_BRANCH_DASH_OPPS_BY_CHANNEL_DOWNLOAD_FILE_NAME
      : DOWNLOAD_FILE_NAME;
  }
};
